/**
 * Cart drawer helpers: discount codes + refresh/open after ATC.
 */
(function () {
  'use strict';

  function root() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  function routes() {
    return window.routes || {};
  }

  function cartAddUrl() {
    var u = (routes().cart_add_url || root() + 'cart/add').replace(/\.js$/, '') + '.js';
    return u;
  }

  var CHECKOUT_GATE_MS = 400;
  var DISCOUNT_GATE_MS = 800;

  function withTimeout(promise, ms) {
    return Promise.race([
      Promise.resolve(promise).catch(function () {
        return null;
      }),
      new Promise(function (resolve) {
        setTimeout(resolve, ms);
      }),
    ]);
  }

  /** Shopify Theme Editor iframe blocks /checkout navigation. */
  function isThemeEditor() {
    try {
      return Boolean(window.Shopify && Shopify.designMode);
    } catch (e) {
      return false;
    }
  }

  /**
   * Navigate to checkout after a short InitiateCheckout attempt.
   * Tracking must never block checkout — hung cart.js used to drop completions.
   * opts.ic — optional IC overrides (value in cents, contentIds, numItems)
   * opts.track — set false to skip IC (rare)
   */
  function goToCheckout(opts) {
    opts = opts || {};
    if (isThemeEditor()) {
      return refreshCartDrawer({ open: true }).then(function () {
        return { editorBlocked: true };
      });
    }
    var checkoutUrl = (routes().checkout_url || root() + 'checkout').replace(/\/$/, '') || '/checkout';

    var trackPromise = Promise.resolve(null);
    if (opts.track !== false && window.TBP && typeof window.TBP.prepareAndTrackCheckout === 'function') {
      trackPromise = window.TBP.prepareAndTrackCheckout(opts.ic || {});
    } else if (opts.track !== false && window.TBP && typeof window.TBP.trackInitiateCheckout === 'function') {
      trackPromise = Promise.resolve(window.TBP.trackInitiateCheckout(opts.ic || {}));
    }

    return withTimeout(trackPromise, CHECKOUT_GATE_MS).then(function () {
      window.location.href = checkoutUrl;
      return { editorBlocked: false };
    });
  }

  /**
   * Update only the header cart link contents — never replace the header section.
   * (Previously closest('.shopify-section') wiped the entire TBP header.)
   */
  function updateCartIconBubble(sectionHtml) {
    var bubble = document.getElementById('cart-icon-bubble');
    if (!bubble || !sectionHtml) return;

    var doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    var nextLink = doc.getElementById('cart-icon-bubble');
    var source = nextLink || doc.querySelector('.shopify-section') || doc.body;
    if (!source) return;

    bubble.innerHTML = nextLink ? nextLink.innerHTML : source.innerHTML;

    var count = 0;
    var badge = bubble.querySelector('#cart-icon-bubble-count, .tbp-header__badge, .cart-count-bubble span[aria-hidden]');
    if (badge) {
      count = parseInt(badge.textContent, 10) || 0;
    } else if (!bubble.querySelector('.tbp-header__badge, .cart-count-bubble')) {
      count = 0;
    }
    if (count > 0) {
      bubble.setAttribute(
        'aria-label',
        'Shopping cart, ' + count + (count === 1 ? ' item' : ' items')
      );
    } else {
      bubble.setAttribute('aria-label', 'Shopping cart');
    }
  }

  /**
   * Re-render cart drawer + bubble from Section Rendering API.
   * If the drawer is already open, keep it open (discount apply must not close it).
   * Pass open:true to force-open (ATC). open:false only skips opening a closed drawer.
   */
  function refreshCartDrawer(opts) {
    opts = opts || {};
    var drawer = document.querySelector('cart-drawer');
    var wasOpen = !!(drawer && drawer.classList.contains('active'));
    var shouldOpen = opts.open === true || wasOpen;

    var url = root() + '?sections=cart-drawer,cart-icon-bubble';
    return fetch(url, { credentials: 'same-origin' })
      .then(function (r) {
        return r.json();
      })
      .then(function (sections) {
        drawer = document.querySelector('cart-drawer');
        if (drawer && sections['cart-drawer']) {
          var doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
          var nextInner = doc.querySelector('#CartDrawer') || doc.querySelector('.drawer__inner');
          var curMount = drawer.querySelector('#CartDrawer');

          if (!shouldOpen) {
            try {
              if (typeof removeTrapFocus === 'function') {
                removeTrapFocus(drawer.activeElement);
              }
            } catch (e) {}
            document.body.classList.remove('overflow-hidden');
            drawer.classList.remove('active', 'animate');
          }

          if (curMount && nextInner) {
            if (nextInner.id === 'CartDrawer') {
              curMount.innerHTML = nextInner.innerHTML;
            } else {
              var curInner = drawer.querySelector('.drawer__inner');
              if (curInner) curInner.replaceWith(nextInner);
            }
          }

          var hasItems = !!drawer.querySelector('.cart-item');
          drawer.classList.toggle('is-empty', !hasItems);
          var itemsEl = drawer.querySelector('cart-drawer-items');
          if (itemsEl) itemsEl.classList.toggle('is-empty', !hasItems);

          if (hasItems) {
            drawer.querySelectorAll('.drawer__inner-empty').forEach(function (el) {
              el.remove();
            });
          }

          var overlay = drawer.querySelector('#CartDrawer-Overlay');
          if (overlay) {
            overlay.addEventListener('click', function () {
              drawer.close();
            });
          }

          if (shouldOpen && typeof drawer.open === 'function') {
            if (drawer.classList.contains('active')) {
              document.body.classList.add('overflow-hidden');
              try {
                var focusRoot = document.getElementById('CartDrawer');
                var focusEl = drawer.querySelector('.drawer__inner') || drawer.querySelector('.drawer__close');
                if (typeof trapFocus === 'function' && focusRoot) trapFocus(focusRoot, focusEl);
              } catch (e) {}
            } else {
              drawer.open();
            }
          } else if (!shouldOpen) {
            document.body.classList.remove('overflow-hidden');
          }
        }

        if (sections['cart-icon-bubble']) {
          updateCartIconBubble(sections['cart-icon-bubble']);
        }

        bindAll();
        if (window.TBP && typeof window.TBP.refreshKlarna === 'function') {
          window.TBP.refreshKlarna();
        }
        return refreshCartPage().then(function () {
          return sections;
        });
      });
  }

  function refreshCartPage() {
    var items = document.getElementById('main-cart-items');
    var footer = document.getElementById('main-cart-footer');
    if (!items && !footer) return Promise.resolve(null);

    var ids = [];
    if (items && items.dataset.id) ids.push(items.dataset.id);
    if (footer && footer.dataset.id) ids.push(footer.dataset.id);
    if (!ids.length) return Promise.resolve(null);

    return fetch(root() + '?sections=' + ids.join(','), { credentials: 'same-origin' })
      .then(function (r) {
        return r.json();
      })
      .then(function (sections) {
        function swapInner(mount, html) {
          if (!mount || !html) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var next = doc.querySelector('.js-contents');
          var cur = mount.querySelector('.js-contents');
          if (next && cur) cur.innerHTML = next.innerHTML;
        }

        if (items && items.dataset.id) swapInner(items, sections[items.dataset.id]);
        if (footer && footer.dataset.id) swapInner(footer, sections[footer.dataset.id]);

        var rootEl = document.querySelector('cart-items');
        var hasItems = !!(items && items.querySelector('.cart-item'));
        if (rootEl) rootEl.classList.toggle('is-empty', !hasItems);
        if (footer) footer.classList.toggle('is-empty', !hasItems);

        bindAll();
        return sections;
      })
      .catch(function () {
        return null;
      });
  }

  /**
   * Add line(s) then refresh + open drawer (or checkout).
   * body: { items: [{id, quantity}] } or { id, quantity }
   */
  function addToCartAndRefresh(body, opts) {
    opts = opts || {};
    var payload = body;
    if (payload && payload.id && !payload.items) {
      payload = { items: [{ id: Number(payload.id), quantity: payload.quantity || 1 }] };
    }

    return withTimeout(rememberLiveDiscount(), DISCOUNT_GATE_MS)
      .catch(function () {})
      .then(function () {
        return fetch(cartAddUrl(), {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        });
      })
      .then(function (r) {
        return r.json().then(function (json) {
          if (!r.ok || (json.status && json.status >= 400)) {
            throw new Error(json.description || json.message || 'Could not add to cart');
          }
          return json;
        });
      })
      .then(function (json) {
        // Restore the live/newest code after ATC — never clobber it with
        // a stale CHEEKY10 left in localStorage from an old signup.
        return withTimeout(reapplyStoredDiscount(), DISCOUNT_GATE_MS).then(function () {
          var before =
            opts.checkout && typeof opts.beforeCheckout === 'function'
              ? Promise.resolve(opts.beforeCheckout(json)).catch(function () {})
              : Promise.resolve();

          return before.then(function () {
            if (opts.checkout) {
              return goToCheckout({ ic: opts.ic }).then(function (nav) {
                if (nav && nav.editorBlocked) {
                  json = json || {};
                  json.editorBlocked = true;
                }
                return json;
              });
            }
            return refreshCartDrawer({
              open: opts.open !== false && !document.getElementById('main-cart-items'),
            }).then(function () {
              return json;
            });
          });
        });
      });
  }

  window.TBP = window.TBP || {};
  window.TBP.refreshCartDrawer = refreshCartDrawer;
  window.TBP.addToCartAndRefresh = addToCartAndRefresh;
  window.TBP.cartAddUrl = cartAddUrl;
  window.TBP.isThemeEditor = isThemeEditor;
  window.TBP.goToCheckout = goToCheckout;
  window.TBP.applyDiscountCode = applyDiscountCode;
  window.TBP.reapplyStoredDiscount = reapplyStoredDiscount;

  function setDiscountStatus(wrap, type, message) {
    var el = wrap.querySelector('[data-tbp-discount-status]');
    if (!el) return;
    el.hidden = !message;
    el.classList.toggle('is-error', type === 'error');
    el.classList.toggle('is-success', type === 'success');
    el.textContent = message || '';
  }

  function titleMatchesCode(title, want) {
    var t = String(title || '')
      .trim()
      .toLowerCase();
    return t === want || (want.length >= 3 && t.indexOf(want) !== -1);
  }

  function cartHasDiscountCode(cart, code) {
    if (!cart || !code) return false;
    var want = String(code).trim().toLowerCase();
    if (!want) return false;
    var codes = cart.discount_codes || [];
    for (var i = 0; i < codes.length; i++) {
      if (String(codes[i].code || '').toLowerCase() === want && codes[i].applicable !== false) {
        return true;
      }
    }
    var apps = cart.cart_level_discount_applications || [];
    for (var j = 0; j < apps.length; j++) {
      if (titleMatchesCode(apps[j].title || apps[j].code, want)) return true;
    }
    var items = cart.items || [];
    for (var k = 0; k < items.length; k++) {
      var discs = items[k].discounts || [];
      for (var d = 0; d < discs.length; d++) {
        if (titleMatchesCode(discs[d].title, want)) return true;
      }
      var allocs = items[k].line_level_discount_allocations || [];
      for (var a = 0; a < allocs.length; a++) {
        var app = allocs[a].discount_application || allocs[a];
        if (titleMatchesCode(app.title || app.code, want)) return true;
      }
    }
    if (readDiscountCookie().toLowerCase() === want && Number(cart.total_discount || 0) > 0) {
      return true;
    }
    return false;
  }

  function fetchCart() {
    return fetch(root() + 'cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then(function (r) {
      return r.json();
    });
  }

  function persistStoredDiscount(code) {
    try {
      sessionStorage.setItem('tbp-subscriber-discount', code);
      localStorage.setItem('tbp-subscriber-discount', code);
    } catch (e) {}
  }

  function getStoredDiscount() {
    try {
      return (
        sessionStorage.getItem('tbp-subscriber-discount') ||
        localStorage.getItem('tbp-subscriber-discount') ||
        ''
      );
    } catch (e) {
      return '';
    }
  }

  /** Shopify session cookie set by /discount/CODE (Omnisend unique codes land here). */
  function readDiscountCookie() {
    try {
      var parts = String(document.cookie || '').split(';');
      for (var i = 0; i < parts.length; i++) {
        var p = parts[i].trim();
        if (p.indexOf('discount_code=') !== 0) continue;
        var raw = p.slice('discount_code='.length);
        try {
          raw = decodeURIComponent(raw);
        } catch (e) {}
        return String(raw || '').trim();
      }
    } catch (e) {}
    return '';
  }

  /**
   * Code currently on the Ajax cart. Ignore automatic/script titles
   * (e.g. "Backup cover 30% off") — those are not codes to re-apply.
   */
  function firstDiscountCodeFromCart(cart) {
    if (!cart) return '';
    var codes = cart.discount_codes || [];
    for (var i = 0; i < codes.length; i++) {
      var c = String(codes[i].code || '').trim();
      if (c && codes[i].applicable !== false) return c;
    }
    var apps = cart.cart_level_discount_applications || [];
    for (var j = 0; j < apps.length; j++) {
      var type = String(apps[j].type || '').toLowerCase();
      if (type && type !== 'discount_code') continue;
      var title = String(apps[j].code || apps[j].title || '').trim();
      if (title) return title;
    }
    var items = cart.items || [];
    for (var k = 0; k < items.length; k++) {
      var discs = items[k].discounts || [];
      for (var d = 0; d < discs.length; d++) {
        var dt = String(discs[d].title || '').trim();
        if (dt && /^[A-Za-z0-9_-]{2,40}$/.test(dt)) return dt;
      }
    }
    return '';
  }

  function liveDiscountCode(cart) {
    return firstDiscountCodeFromCart(cart) || readDiscountCookie();
  }

  /** Newest applied code wins over a stale stored CHEEKY10. */
  function rememberLiveDiscount() {
    return fetchCart()
      .then(function (cart) {
        var live = liveDiscountCode(cart);
        if (live) persistStoredDiscount(live);
        return live || getStoredDiscount() || null;
      })
      .catch(function () {
        var cookie = readDiscountCookie();
        if (cookie) persistStoredDiscount(cookie);
        return cookie || getStoredDiscount() || null;
      });
  }

  function forgetStoredDiscount() {
    try {
      sessionStorage.removeItem('tbp-subscriber-discount');
      localStorage.removeItem('tbp-subscriber-discount');
    } catch (e) {}
  }

  function applyDiscountCode(code, opts) {
    var c = (code || '').trim();
    if (!c) return Promise.reject(new Error('Enter a discount code'));
    opts = opts || {};

    // 1) /discount/CODE sets Shopify discount session cookie (works at checkout)
    // 2) cart/update.js attaches code to the Ajax cart when supported
    var tasks = [
      fetch(root() + 'discount/' + encodeURIComponent(c), {
        credentials: 'same-origin',
        redirect: 'manual',
      }).catch(function () {}),
      fetch(root() + 'cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: c }),
      }).catch(function () {}),
    ];

    return Promise.all(tasks)
      .then(function () {
        return fetchCart().catch(function () {
          return null;
        });
      })
      .then(function (cart) {
        var applied = cartHasDiscountCode(cart, c);
        if (applied || opts.forcePersist) {
          // forcePersist: subscriber/CRO codes may only attach via /discount cookie
          // and not appear on cart.discount_codes until checkout.
          persistStoredDiscount(c);
        } else if (opts.requireApplied !== false && !opts.fromStorage) {
          // Cart Apply box: do not persist typos. Do not wipe an existing stored code.
          if (!opts.skipRefresh) {
            return refreshCartDrawer({ open: true }).then(function () {
              var err = new Error('Code not applied — check spelling');
              err.code = 'NOT_APPLIED';
              throw err;
            });
          }
          var err2 = new Error('Code not applied — check spelling');
          err2.code = 'NOT_APPLIED';
          return Promise.reject(err2);
        } else {
          persistStoredDiscount(c);
        }
        if (opts.skipRefresh) return c;
        return refreshCartDrawer({ open: opts.open === true }).then(function () {
          return c;
        });
      });
  }

  /** Fallback if theme.liquid bounce missed — navigate so Shopify sets the cookie. */
  function applyDiscountFromUrl() {
    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (e) {
      return;
    }
    var code = (params.get('discount') || params.get('promo') || params.get('coupon') || '').trim();
    if (!code || !/^[A-Za-z0-9_-]{2,40}$/.test(code)) return;
    if (location.pathname.indexOf('/discount/') !== -1) return;

    persistStoredDiscount(code);

    try {
      var dest = new URL(window.location.href);
      dest.searchParams.delete('discount');
      dest.searchParams.delete('promo');
      dest.searchParams.delete('coupon');
      window.location.replace(
        root() + 'discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(dest.pathname + dest.search + dest.hash)
      );
    } catch (e) {
      applyDiscountCode(code, { open: false, forcePersist: true, skipRefresh: true }).catch(function () {});
    }
  }

  /**
   * Re-apply a discount after ATC only if the cart/cookie no longer has one.
   * If a newer code is already live (Omnisend unique, cart Apply, /discount/CODE),
   * keep it and update storage — do not restore an older CHEEKY10.
   */
  function reapplyStoredDiscount() {
    return fetchCart()
      .then(function (cart) {
        var live = liveDiscountCode(cart);
        if (live) {
          persistStoredDiscount(live);
          return live;
        }
        var stored = getStoredDiscount();
        if (!stored) return null;
        return applyDiscountCode(stored, {
          skipRefresh: true,
          forcePersist: true,
          fromStorage: true,
        });
      })
      .catch(function () {
        return null;
      });
  }

  function clearDiscountCookie() {
    try {
      document.cookie = 'discount_code=; Max-Age=0; Path=/; SameSite=Lax';
      document.cookie = 'discount_code=; Max-Age=0; Path=/';
    } catch (e) {}
  }

  function clearDiscountCode() {
    forgetStoredDiscount();
    clearDiscountCookie();

    // Clear Ajax cart discount + best-effort clear of /discount/CODE session
    var tasks = [
      fetch(root() + 'cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: '' }),
      }).catch(function () {}),
      // Hitting discount with a blank-ish path helps drop the storefront discount cookie
      fetch(root() + 'discount/%20', {
        credentials: 'same-origin',
        redirect: 'manual',
      }).catch(function () {}),
    ];

    return Promise.all(tasks).then(function () {
      clearDiscountCookie();
      return refreshCartDrawer({ open: true });
    });
  }

  function onDiscountClick(e) {
    var clearBtn = e.target.closest('[data-tbp-discount-clear]');
    if (clearBtn) {
      var clearWrap = clearBtn.closest('[data-tbp-cart-discount]');
      clearBtn.disabled = true;
      clearDiscountCode()
        .catch(function () {})
        .finally(function () {
          clearBtn.disabled = false;
        });
      return;
    }

    var btn = e.target.closest('[data-tbp-discount-apply]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var wrap = btn.closest('[data-tbp-cart-discount]');
    if (!wrap) return;
    var input = wrap.querySelector('[data-tbp-discount-input]');
    var code = input ? input.value : '';
    btn.disabled = true;
    btn.textContent = 'Applying…';
    setDiscountStatus(wrap, '', '');
    applyDiscountCode(code, { open: !document.getElementById('main-cart-items') })
      .then(function () {
        var next = document.querySelector('[data-tbp-cart-discount]');
        if (next && next.querySelector('[data-tbp-discount-applied]')) {
          setDiscountStatus(next, 'success', 'Discount applied');
        }
      })
      .catch(function (err) {
        var next = document.querySelector('[data-tbp-cart-discount]');
        if (next) setDiscountStatus(next, 'error', (err && err.message) || 'Could not apply code');
      })
      .finally(function () {
        var nextBtn = document.querySelector('[data-tbp-discount-apply]');
        if (nextBtn) {
          nextBtn.disabled = false;
          nextBtn.textContent = 'Apply';
        }
      });
  }

  function onDiscountKey(e) {
    if (e.key !== 'Enter') return;
    var input = e.target.closest('[data-tbp-discount-input]');
    if (!input) return;
    e.preventDefault();
    var wrap = input.closest('[data-tbp-cart-discount]');
    var btn = wrap && wrap.querySelector('[data-tbp-discount-apply]');
    if (btn) btn.click();
  }

  function parseVariants(wrap) {
    var script = wrap.querySelector('[data-tbp-variant-data]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      return [];
    }
  }

  function selectedOptions(wrap) {
    var opts = [];
    wrap.querySelectorAll('[data-tbp-cart-option]').forEach(function (sel) {
      opts[Number(sel.getAttribute('data-option-index') || 0)] = sel.value;
    });
    return opts;
  }

  function findVariant(variants, options) {
    return variants.find(function (v) {
      var vo = v.options || [];
      for (var i = 0; i < options.length; i++) {
        if ((vo[i] || '') !== (options[i] || '')) return false;
      }
      return true;
    });
  }

  function changeLineVariant(wrap) {
    var lineKey = wrap.getAttribute('data-line-key');
    var qty = Number(wrap.getAttribute('data-quantity') || 1) || 1;
    var variants = parseVariants(wrap);
    var options = selectedOptions(wrap);
    var match = findVariant(variants, options);

    if (!match) {
      alert('That combination isn’t available.');
      return;
    }
    if (!match.available) {
      alert('That option is sold out.');
      return;
    }

    wrap.classList.add('is-loading');

    var changeUrl = routes().cart_change_url || root() + 'cart/change.js';
    var addUrl = routes().cart_add_url || root() + 'cart/add.js';

    fetch(changeUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0 }),
    })
      .then(function (r) {
        return r.json();
      })
      .then(function () {
        return fetch(addUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: match.id, quantity: qty }],
          }),
        });
      })
      .then(function (r) {
        return r.json();
      })
      .then(function (json) {
        if (json.status && json.status >= 400) {
          throw new Error(json.description || json.message || 'Could not update item');
        }
        return refreshCartDrawer();
      })
      .catch(function (err) {
        alert((err && err.message) || 'Could not update item');
        wrap.classList.remove('is-loading');
      });
  }

  function onVariantChange(e) {
    var sel = e.target.closest('[data-tbp-cart-option]');
    if (!sel) return;
    var wrap = sel.closest('[data-tbp-cart-variants]');
    if (!wrap) return;
    // Don't let Dawn cart-drawer-items treat selects as quantity inputs
    e.stopPropagation();
    changeLineVariant(wrap);
  }

  function openHeaderCart(link, event) {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.open !== 'function') return false;
    if (event) event.preventDefault();
    drawer.open(link);
    return true;
  }

  function bindCartIcon() {
    if (document.documentElement.dataset.tbpCartIconBound === '1') return;
    document.documentElement.dataset.tbpCartIconBound = '1';

    document.addEventListener(
      'click',
      function (e) {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        var link = e.target.closest && e.target.closest('#cart-icon-bubble, a.tbp-header__cart');
        if (!link) return;
        openHeaderCart(link, e);
      },
      true
    );

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var link = e.target.closest && e.target.closest('#cart-icon-bubble, a.tbp-header__cart');
      if (!link) return;
      openHeaderCart(link, e);
    });
  }

  function bindAll() {
    bindCartIcon();
    document.querySelectorAll('[data-tbp-cart-discount]').forEach(function (wrap) {
      if (wrap.dataset.tbpBound) return;
      wrap.dataset.tbpBound = '1';
      wrap.addEventListener('click', onDiscountClick);
      wrap.addEventListener('keydown', onDiscountKey);
    });

    document.querySelectorAll('[data-tbp-cart-variants]').forEach(function (wrap) {
      if (wrap.dataset.tbpBound) return;
      wrap.dataset.tbpBound = '1';
      wrap.addEventListener('change', onVariantChange);
    });
  }

  function init() {
    bindAll();
    applyDiscountFromUrl();
    rememberLiveDiscount().catch(function () {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('cart:refresh', function () {
    bindAll();
  });
  document.addEventListener('shopify:section:load', function () {
    bindAll();
  });
  if (typeof subscribe === 'function') {
    subscribe('cart-update', function () {
      bindAll();
    });
  }
})();
